import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Clock, Play } from "lucide-react";
import { useTranslation } from "react-i18next";
import { extractYouTubeId } from "@/lib/youtube";

export type TutorialLevel = "beginner" | "intermediate" | "advanced";

export interface TutorialCardProps {
  title: string;
  src: string;
  time: string;
  level: TutorialLevel;
}

const getLevelBadgeClasses = (level: TutorialLevel): string => {
  switch (level) {
    case "beginner":
      return "bg-green-500/90 text-white";
    case "intermediate":
      return "bg-yellow-500/90 text-white";
    case "advanced":
      return "bg-red-500/90 text-white";
    default:
      return "bg-gray-500/90 text-white";
  }
};

export const TutorialCard = ({ title, src, time, level }: TutorialCardProps) => {
  const { t } = useTranslation();
  const videoId = extractYouTubeId(src);
  const thumbnail = videoId ? `https://img.youtube.com/vi/${videoId}/hqdefault.jpg` : "/images/autoclipper-logo-full.png";

  return (
    <Card className="group cursor-pointer hover:shadow-lg transition-all duration-300 hover:scale-105">
      <a href={src} target="_blank" rel="noopener noreferrer" className="block">
        <CardHeader className="p-0">
          <div className="relative">
            <img
              src={thumbnail}
              alt={title}
              className="w-full h-48 object-cover rounded-t-lg"
            />
            <div className="absolute inset-0 bg-black/20 group-hover:bg-black/40 transition-colors duration-300 rounded-t-lg flex items-center justify-center">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <Play className="w-6 h-6 text-white ml-1" />
              </div>
            </div>
            <div className="absolute top-3 left-3">
              <Badge className={getLevelBadgeClasses(level)}>
                {t(`tutorialCard.${level}`)}
              </Badge>
            </div>
            <div className="absolute bottom-3 right-3 bg-black/60 backdrop-blur-sm rounded-md px-2 py-1 flex items-center gap-1">
              <Clock className="w-3 h-3 text-white" />
              <span className="text-xs text-white">{time}</span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-4">
          <CardTitle className="text-lg mb-2 group-hover:text-primary transition-colors">
            {title}
          </CardTitle>
          <CardDescription className="text-sm">
            {t('tutorialCard.clickToWatch')}
          </CardDescription>
        </CardContent>
      </a>
    </Card>
  );
};

export default TutorialCard;


